import React from 'react';
import { Platform } from '../../../web/react-native';
import styled from '../../../components/styled';
import { FieldInputProps, useField, useFormikContext } from 'formik';

import { SnippetConfigurationColumn, SnippetDataDictionary } from '../../../services/dataModels';
import {
  TextField,
  RegularText,
  DateTimeField,
  DateTimeFieldMode,
  Selector,
  Toggle,
  RichTextField,
} from '../../../components';
import { selectEditingMode } from '../../../store';
import { formatControlValue } from '../../../helpers/Functions';

interface DataFieldProps {
  parent: {
    field: FieldInputProps<any>;
    path: string;
  };
  configuration: SnippetConfigurationColumn;
  value: string;
  disabled: boolean;
}

const Field = ({ parent, configuration, value, disabled }: DataFieldProps) => {
  const name = `${parent.path}[${configuration.name}]`;
  const { controlType: type, displayName: title } = configuration;
  const { setFieldValue } = useFormikContext();
  const readOnly = selectEditingMode.useHook() === 'readOnly' || disabled;

  switch (type) {
    case 'Checkbox':
      return (
        <Toggle
          name={name}
          value={value && value.trim().toLocaleUpperCase() === 'TRUE'}
          disabled={readOnly}
          onValueChange={setFieldValue}
        />
      );
    case 'Date':
    case 'Time':
    case 'DateTime':
      if (readOnly) {
        return <Text>{formatControlValue(type, value)}</Text>;
      }

      return (
        <DateTimeField
          name={name}
          mode={type.toLowerCase() as DateTimeFieldMode}
          value={value}
          onValueChange={setFieldValue}
        />
      );

    case 'Static Text':
      return <Text>{value}</Text>;

    case 'Dropdown':
      if (readOnly) {
        return <Text>{value}</Text>;
      }
      return (
        <Selector
          name={name}
          title={title}
          value={{ key: value, value: value }}
          items={configuration.controlValues.map((value) => {
            return { key: value, value: value };
          })}
          onValueChange={setFieldValue}
        />
      );

    case 'Text - Single Line':
      if (readOnly) {
        return <Text>{value}</Text>;
      }

      return <TextField value={value} onChangeText={parent.field.onChange(name)} />;

    case 'Text':
      return <RichTextField
        value={value}
        onChangeText={parent.field.onChange(name)}
        disabled={readOnly}
      />;

    case 'Numeric':
      if (readOnly) {
        return <Text>{value}</Text>;
      }

      return (
        <TextField
          value={value}
          keyboardType={Platform.OS === 'ios' ? 'numbers-and-punctuation' : 'visible-password'}
          onChangeText={parent.field.onChange(name)}
          multiline={false}
        />
      );

    default:
      return <TextField value={value} disabled={readOnly} />;
  }
};

const DataField = ({ parent, configuration, value, disabled }: DataFieldProps) => {
  const name = `${parent.path}[${configuration.name}]`;
  const [, meta] = useField(name);
  const { controlType: type, displayName: title } = configuration;

  if (type === 'Checkbox') {
    return (
      <View>
        <Spacer />
        <ToggleContainer>
          <SubHeader>{title}</SubHeader>
          <Field parent={parent} configuration={configuration} value={value} disabled={disabled} />
        </ToggleContainer>
        <Spacer />
      </View>
    );
  }

  return (
    <View>
      <Spacer />
      <SubHeader>{title}</SubHeader>
      <Field parent={parent} configuration={configuration} value={value} disabled={disabled} />
      {meta.error ? <ErrorText>{meta.error ?? ' '}</ErrorText> : <></>}
      <Spacer />
    </View>
  );
};

export interface DataRowProps {
  path: string;
  configuration: SnippetConfigurationColumn[];
  data: SnippetDataDictionary;
  disabled?: string[];
}

export const DataRow = ({ path, configuration, data, disabled }: DataRowProps) => {
  const [field] = useField(path);

  return (
    <Container>
      {configuration.map((c, i) => (
        <DataField
          key={i}
          parent={{ field, path }}
          configuration={c}
          value={(data[c.name] ?? '') + ''}
          disabled={disabled?.includes(c.name) ?? false}
        />
      ))}
    </Container>
  );
};

const Container = styled.View`
  padding-horizontal: 10px;
`;

const ToggleContainer = styled.View`
  flex-direction: row !important;
`;

const SubHeader = styled.Text`
  font-weight: 500;
  margin-bottom: 7px;
  flex: 1;
`;

const View = styled.View``;

const Text = styled.Text`
  flex: 1;
`;

const Spacer = styled.View`
  margin: 5px;
`;

const ErrorText = styled(RegularText)`
  color: red;
  background-color: rgba(255, 255, 255, 1);
`;
